import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';

const AssistantsGovernanceAccounting: React.FC = () => {
  const commitments = [
    'The assistant will always identify itself as an AI, not a human advisor',
    'The assistant will not provide specific tax advice—it will direct users to qualified professionals',
    'Client data is encrypted in transit and at rest using industry-standard protocols',
    'Conversation logs are retained for 12 months for audit purposes, then securely deleted',
    'The assistant will escalate to a human when confidence is low or the query is out of scope',
    'All responses are grounded in approved, audited source material',
  ];

  const boundaries = [
    'Will NOT provide binding tax calculations or specific financial advice',
    'Will NOT access or modify client financial records directly',
    'Will NOT make promises about outcomes or guarantees',
    'Will NOT share information between different clients',
    'Will NOT process payments or handle sensitive banking details',
  ];

  const dataHandling = [
    { label: 'Data Location', value: 'New Zealand / Australia data centers' },
    { label: 'Encryption', value: 'AES-256 at rest, TLS 1.3 in transit' },
    { label: 'Retention Period', value: '12 months (configurable)' },
    { label: 'Third-Party Sharing', value: 'None without explicit consent' },
    { label: 'Compliance', value: 'NZ Privacy Act 2020, GDPR-aligned' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Accounting Trust Charter
              </h1>
              <p className="text-xl text-white/90">
                This document outlines the governance commitments, boundaries, and safeguards 
                for governed AI assistants deployed to accounting practice websites.
              </p>
            </div>
          </div>
        </section>

        {/* Version Info */}
        <section className="py-6 bg-muted/50 border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Version 1.0</span>
              </div>
              <div>Last Updated: February 2026</div>
              <div>Industry: Accounting & Financial Services</div>
            </div>
          </div>
        </section>

        {/* Commitments */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-foreground">Our Commitments</h2>
              <p className="text-muted-foreground mb-8">
                Every Advisory Assistant deployed for accounting practices operates under these binding commitments:
              </p>
              <div className="space-y-4">
                {commitments.map((commitment, index) => (
                  <div key={index} className="flex items-start gap-4 bg-card border border-border rounded-lg p-4">
                    <CheckCircle className="h-6 w-6 text-brand-teal flex-shrink-0 mt-0.5" />
                    <span className="text-card-foreground">{commitment}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Boundaries */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-foreground">Defined Boundaries</h2>
              <p className="text-muted-foreground mb-8">
                To maintain professional standards and protect your clients, the assistant will NOT:
              </p>
              <div className="space-y-4">
                {boundaries.map((boundary, index) => (
                  <div key={index} className="flex items-start gap-4 bg-card border border-amber-200 rounded-lg p-4">
                    <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-card-foreground">{boundary}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Data Handling */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-foreground">Data Handling & Security</h2>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <tbody>
                    {dataHandling.map((item, index) => (
                      <tr key={index} className={index !== dataHandling.length - 1 ? 'border-b border-border' : ''}>
                        <td className="px-6 py-4 font-medium text-card-foreground bg-muted/50 w-1/3">
                          {item.label}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {item.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Escalation */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-foreground">Escalation Protocol</h2>
                  <div className="bg-muted/50 rounded-lg p-6">
                    <p className="text-muted-foreground mb-6">
                      The assistant is programmed to escalate conversations to human staff when:
                    </p>
                <ul className="space-y-3 mb-6">
                  {[
                    'The query involves specific tax calculations or binding advice',
                    'The user expresses frustration or dissatisfaction',
                    'The confidence level for a response is below threshold',
                    'The topic is outside the assistant\'s trained scope',
                    'The user explicitly requests human assistance',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-card-foreground">
                      <span className="w-2 h-2 bg-brand-teal rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">
                  Escalated conversations are logged and reviewed as part of monthly governance reporting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Deploy a Governed Assistant?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Get an Advisory Assistant built on these governance principles for your accounting practice.
            </p>
            <Link to="/assistants/get-assistant">
              <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                Get Your Assistant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsGovernanceAccounting;
